import { ApolloError, gql } from "@apollo/client";
import { Intent } from "@blueprintjs/core";
import { SpecialVersionType } from "@hex/common";
import React, { useCallback } from "react";
import styled from "styled-components";

import { HexButton } from "../../hex-components";
import { SyncRepositoryHexVersionLinkMpFragment } from "../../hex-version-multiplayer/HexVersionMPModel.generated";
import { HexVersionFragment } from "../../mutations/hex.generated";
import { ORG_ID } from "../../orgs";
import { Routes } from "../../route/routes";
import { useProjectContext } from "../../util/projectContext";
import { HexToaster } from "../common/Toasts";
import { WarningIcon } from "../icons/CustomIcons";

import { generateCompareUrl } from "./ProjectGithubSyncPanel";
import { useSyncRepoByIdQuery } from "./Toasts.generated";

export const remoteChangesExistKey = "remote-changes-exist";
export const syncErrorKey = "sync-error";
export const unmergedChangesKey = "unmerged-changes";

const Wrapper = styled.div`
  color: ${({ theme }) => theme.backgroundColor.DEFAULT};
`;

const HeaderDiv = styled.div`
  display: flex;
  align-items: center;

  font-size: ${({ theme }) => theme.fontSize.DEFAULT};
`;

const Message = styled.div`
  margin-top: 10px;
  margin-left: 26px;

  opacity: 0.75;
`;

const StyledWarningIcon = styled(WarningIcon)`
  margin-right: 12px;
`;

const Link = styled.a`
  text-decoration: underline;

  opacity: unset;
`;

gql`
  query SyncRepoById($syncRepositoryId: SyncRepositoryId!) {
    syncRepositoryById(syncRepositoryId: $syncRepositoryId) {
      ...SyncRepoFragment
    }
  }
`;

interface RemoteChangesWarningProps {
  mode: "publish" | "save";
  forceAction: () => void;
}

const RemoteChangesWarning: React.ComponentType<RemoteChangesWarningProps> =
  React.memo(function RemoteChangesWarning(props: RemoteChangesWarningProps) {
    const { forceAction, mode } = props;
    return (
      <Wrapper>
        <HeaderDiv>
          <StyledWarningIcon />
          Your project is out of sync
        </HeaderDiv>
        <Message>
          We were unable to {mode} your project because the remote contains work
          that you do not have locally. Please{" "}
          <Link onClick={forceAction}>{mode} and force push</Link> (this will
          overwrite your work on your remote branch!), or pull the latest from
          your remote repository to get back in sync.
        </Message>
      </Wrapper>
    );
  });

interface SyncErrorProps {
  mode: "publish" | "save";
}

const SyncError: React.ComponentType<SyncErrorProps> = React.memo(
  function SyncError(props: SyncErrorProps) {
    const { mode } = props;
    return (
      <Wrapper>
        <HeaderDiv>
          <StyledWarningIcon />
          We encountered a repository sync error
        </HeaderDiv>
        <Message>
          Your project was {mode === "publish" ? "published" : "saved"}, but we
          hit an error syncing with the remote repository. Try again later or
          reach out to your workspace admin for help.
        </Message>
      </Wrapper>
    );
  },
);

interface UnmergedChangesProps {
  syncRepoLink: SyncRepositoryHexVersionLinkMpFragment;
}

const UnmergedChangesError: React.ComponentType<UnmergedChangesProps> =
  React.memo(function UnmergedChangesError(props: UnmergedChangesProps) {
    const { syncRepoLink } = props;
    const { data, loading } = useSyncRepoByIdQuery({
      variables: { syncRepositoryId: syncRepoLink.syncRepositoryId },
    });
    const { hexId } = useProjectContext();

    const pullRequestCallback = useCallback(() => {
      if (data?.syncRepositoryById) {
        const baseUrl = Routes.href(ORG_ID, true, Routes.LOGIC, {
          hexId,
          version: SpecialVersionType.DRAFT,
        });
        window.open(
          generateCompareUrl({
            repo: data.syncRepositoryById,
            workingBranch: syncRepoLink.branch,
            projectUrl: baseUrl,
          }),
        );
      }
    }, [data?.syncRepositoryById, hexId, syncRepoLink.branch]);
    return (
      <Wrapper>
        <HeaderDiv>
          <StyledWarningIcon />
          Unable to publish
        </HeaderDiv>
        <Message>
          You cannot publish this project until you have merged your changes to
          the main branch.
          <HexButton
            disabled={loading}
            loading={loading}
            onClick={pullRequestCallback}
          >
            PULL REQUEST
          </HexButton>
        </Message>
      </Wrapper>
    );
  });

export interface GenericErrorProps {
  message: string;
}

export const GenericError: React.ComponentType<GenericErrorProps> = React.memo(
  function GenericError(props: GenericErrorProps) {
    const { message } = props;
    return (
      <Wrapper>
        <HeaderDiv>
          <StyledWarningIcon />
          Sync error
        </HeaderDiv>
        <Message>
          <pre>{message}</pre>
        </Message>
      </Wrapper>
    );
  },
);

export interface PullErrorProps {
  message: string;
}

export const PullError: React.ComponentType<PullErrorProps> = React.memo(
  function PullError(props: PullErrorProps) {
    const { message } = props;
    return (
      <Wrapper>
        <HeaderDiv>
          <StyledWarningIcon />
          Unable to pull from branch
        </HeaderDiv>
        <Message>
          <pre>{message}</pre>
        </Message>
      </Wrapper>
    );
  },
);

export function maybePopSyncErrorToast({
  hexVersion,
  mode,
  toaster,
}: {
  hexVersion?: HexVersionFragment;
  toaster: HexToaster;
  mode: "publish" | "save";
}): void {
  if (hexVersion?.vcsSyncError) {
    toaster.show(
      {
        intent: Intent.DANGER,
        message: <SyncError mode={mode} />,
      },
      syncErrorKey,
    );
  }
}

export function maybePopRemoteChangesToast({
  err,
  forceAction,
  mode,
  toaster,
}: {
  forceAction: () => void;
  err: ApolloError;
  toaster: HexToaster;
  mode: "publish" | "save";
}): void {
  if (err.message.includes("Remote changes exist")) {
    toaster.show(
      {
        intent: Intent.WARNING,
        message: <RemoteChangesWarning forceAction={forceAction} mode={mode} />,
        timeout: 0,
      },
      remoteChangesExistKey,
    );
    console.error(err);
  }
}

export function maybePopUnmergedChangesToast({
  err,
  syncRepoLink,
  toaster,
}: {
  err: ApolloError;
  toaster: HexToaster;
  syncRepoLink?: SyncRepositoryHexVersionLinkMpFragment;
}): void {
  if (syncRepoLink && err.message.includes("Unmerged changes")) {
    toaster.show(
      {
        intent: Intent.DANGER,
        message: <UnmergedChangesError syncRepoLink={syncRepoLink} />,
      },
      unmergedChangesKey,
    );
  }
}
